import { useState, useEffect, useRef, type ReactNode } from "react";
import { X, AlertCircle } from "lucide-react";

export interface Tab {
  id: string;
  label: string;
  icon?: ReactNode;
  content: ReactNode;
}

interface ModalWithTabsProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  tabs: Tab[];
  defaultTab?: string;
  size?: "sm" | "md" | "lg" | "xl";
  footer?: ReactNode; // Botones del formulario que se mantienen fuera de los tabs
  onTabChange?: (tabId: string) => void;
  formId?: string; // ID del formulario para envolver todo el contenido
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void; // Handler de submit del formulario
  tabErrors?: Record<string, boolean>; // Mapeo de tabId -> tiene errores
}

export function ModalWithTabs({
  isOpen,
  onClose,
  title,
  tabs,
  defaultTab,
  size = "lg",
  footer,
  onTabChange,
  formId,
  onSubmit,
  tabErrors = {},
}: ModalWithTabsProps) {
  const [activeTab, setActiveTab] = useState<string>(
    defaultTab || tabs[0]?.id || ""
  );
  const formRef = useRef<HTMLFormElement | null>(null);

  // Estado para controlar si debemos cambiar al tab con errores (solo cuando se intenta submit)
  const [shouldFocusErrorTab, setShouldFocusErrorTab] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Reset al tab por defecto solo cuando se abre el modal
      if (defaultTab) {
        setActiveTab(defaultTab);
      } else if (tabs.length > 0) {
        setActiveTab(tabs[0].id);
      }
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, defaultTab]); // Removido 'tabs' de las dependencias para evitar resetear el tab cuando cambia el contenido

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  // Cambiar automáticamente al primer tab con errores cuando se detectan errores después de un submit
  useEffect(() => {
    if (!shouldFocusErrorTab) return;

    const tabsWithErrors = tabs.filter((tab) => tabErrors[tab.id]);
    if (tabsWithErrors.length > 0) {
      const firstTabWithError = tabsWithErrors[0].id;
      if (activeTab !== firstTabWithError) {
        setActiveTab(firstTabWithError);
        onTabChange?.(firstTabWithError);
      }
      setShouldFocusErrorTab(false); // Reset después de cambiar
    }
  }, [tabErrors, tabs, activeTab, onTabChange, shouldFocusErrorTab]);

  if (!isOpen || tabs.length === 0) return null;

  // Manejar submit con detección de errores
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Si hay errores, cambiar al tab con errores pero permitir que react-hook-form maneje la validación
    if (Object.values(tabErrors).some(Boolean)) {
      setShouldFocusErrorTab(true);
    }

    // Llamar al handler original (react-hook-form manejará la validación)
    onSubmit?.(e);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="relative flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-40"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className={`relative w-full ${sizeClasses[size]} bg-white rounded-lg shadow-xl z-50 flex flex-col h-[85vh]`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content with Tabs */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Tabs Sidebar */}
            <div className="w-48 border-r border-gray-200 bg-gray-50 flex-shrink-0 flex flex-col">
              <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
                {tabs.map((tab) => {
                  const hasError = tabErrors[tab.id] || false;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? "bg-[#282828] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      } ${hasError && activeTab !== tab.id ? "border-l-4 border-red-500" : ""}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
                        <span className="text-left truncate">{tab.label}</span>
                      </div>
                      {hasError && (
                        <span
                          className="flex-shrink-0"
                          title="Este tab contiene errores"
                        >
                          <AlertCircle
                            className={`w-4 h-4 ${
                              activeTab === tab.id ? "text-white" : "text-red-500"
                            }`}
                            aria-label="Este tab contiene errores"
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content - Form envuelve todo el contenido para que todos los campos estén en el DOM */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {formId && onSubmit ? (
                <form
                  ref={formRef}
                  id={formId}
                  onSubmit={handleFormSubmit}
                  className="h-full"
                >
                  <div className="p-6 h-full">
                    {/* Renderizar todos los tabs pero ocultar los inactivos para que los campos estén en el DOM */}
                    {tabs.map((tab) => (
                      <div
                        key={tab.id}
                        className={activeTab === tab.id ? "block" : "hidden"}
                      >
                        {tab.content}
                      </div>
                    ))}
                  </div>
                </form>
              ) : (
                <div className="p-6 h-full">
                  {tabs.find((tab) => tab.id === activeTab)?.content}
                </div>
              )}
            </div>
          </div>

          {/* Footer (Botones del formulario) */}
          {footer && (
            <div className="border-t border-gray-200 p-6 flex-shrink-0 bg-gray-50">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

